/**
 * Role-based dashboard configuration.
 * Panel assignments and practice area mappings derived from staffConfig.
 *
 * staffConfig.js is the single source of truth for staff identity AND roles.
 * Do not add role mappings here — add dashboard_role to staffConfig instead.
 *
 * NOTE: ROLES.DIRECTOR is intentionally preserved.
 * It gates: submission deletion, admin tools, directors lounge, backend /user-role is_director.
 * Do NOT remove or rename it.
 *
 * PANEL ASSIGNMENTS (updated April 2026):
 *
 * CreditControlWidget + AccountsTodayPanel → May (accounts) only.
 *   Directors had it layered on until BM-H3660 (5 Oct 2026).
 * GoldenRulePipelinePanel → admin only (Liezel + clientcare).
 *
 * PipelineMiniPanel (home screen pipeline widget):
 *   Replaces my_matters, submissions, onboarding, conflicts tab panels.
 *   Pre-filtered to PIPELINE_PEERS group — full columns, locked filter.
 *   - Rebekah, Emily, Jodie → [RM, EH, JD] (Residential)
 *   - Sangita               → [SG]          (CommProp)
 *   - Vanessa, Himanie      → [VEC, HD]      (CoCo/Lit)
 *   - Liezel, clientcare    → []             (all matters, unfiltered)
 *   - Vanessa (Director)  → [VEC, HD]   (CoCo/Lit — same as Himanie)
 *   - Rebekah (Director)  → [RM, EH, JD] (Resi — via EMAIL_PANEL_OVERRIDES)
 *   - Jonathan, May         → null           (no pipeline widget)
 *
 * DeadlinesNudgesPanel → REMOVED from all home pages.
 *   Users navigate to it directly; daily digest handles nudges.
 *
 * ResiChecklistsPanel → Rebekah, Emily, Jodie only.
 * PostCompletionTasksPanel → Rebekah, Emily, Jodie only.
 * PostIntakeQueuePanel → Jonathan only.
 * FileClosureRiskPanel → admin only (Liezel + clientcare).
 * ACCOUNTS (May): AccountsTodayPanel only.
 */

import { STAFF, getSupervisedInitials } from './staffConfig';

export const ROLES = {
  ADMIN: "admin",
  DIRECTOR: "director",
  RESI_FE: "resi_fe",
  RESI_ASSISTANT: "resi_assistant",
  COMM_PROP: "comm_prop",
  COCO_LIT: "coco_lit",
  POST_SCANNING: "post_scanning",
  ACCOUNTS: "accounts",
};

export const PRACTICE_AREAS = {
  RESI: "001",
  COCO: "002",
  COMM_PROP: "003",
  DISPUTES: "006",
  IP: "007",
};

// Brief 45: a staff member belongs in fee-earner dropdowns when they are
// active AND either a primary fee earner OR explicitly flagged for the
// fee-earner dropdown (is_fe_dropdown). The second clause mirrors the
// backend is_fe_dropdown_role contract, which includes RESI_FE staff such
// as Emily Howard whose is_fee_earner flag is intentionally false (the
// flag gates supervisor/billing logic, not dropdown membership). Without
// this clause Emily was missing from the conflict-check Fee Earner
// dropdown even though she runs the residential pipeline.
export const FEE_EARNERS = STAFF
  .filter(s => s.is_active && (s.is_fee_earner || s.is_fe_dropdown))
  .map(s => ({ name: s.full_name, initials: s.initials }));

// Brief 45: the conflict-check Decision Maker dropdown source. A decision
// maker decides whether a potential conflict is acceptable and must be a
// director or senior/qualified fee earner — never a trainee paralegal or
// support member. Driven by the explicit is_decision_maker capability on
// the STAFF row (set on Vanessa, Rebekah, Himanie, Sangita, Sharon) rather
// than by role, because role cannot separate Himanie (trainee solicitor,
// IS a decision maker) from Raafiq (trainee paralegal, is NOT). This is
// the single source for the dropdown so the page-level list cannot drift.
export const DECISION_MAKERS = STAFF
  .filter(s => s.is_active && s.is_decision_maker)
  .map(s => s.full_name);

// Email → fee earner info (active staff only)
export const USER_FEE_EARNER = Object.fromEntries(
  STAFF.filter(s => s.is_active).map(s => [
    s.email,
    {
      initials: s.initials,
      name: s.full_name,
      nameFragments: s.full_name.toLowerCase().split(/\s+/).concat(s.initials.toLowerCase()),
    },
  ])
);

// Who each admin/senior can see matters for
export const SUPERVISED_FEE_EARNERS = Object.fromEntries(
  STAFF.filter(s => s.supervised_initials.length > 0).map(s => [s.email, s.supervised_initials])
);

export function getUserFeeEarner(email) {
  if (!email) return null;
  return USER_FEE_EARNER[email.toLowerCase().trim()] || null;
}

// Role lookup — derived entirely from staffConfig, no duplicate map
export function getUserRole(email) {
  if (!email) return null;
  const s = STAFF.find(x => x.email === email.toLowerCase().trim());
  return (s?.is_active && s?.dashboard_role) || null;
}

// Role → practice area codes
const ROLE_PRACTICE_AREAS = {
  [ROLES.RESI_FE]:        [PRACTICE_AREAS.RESI],
  [ROLES.RESI_ASSISTANT]: [PRACTICE_AREAS.RESI],
  [ROLES.COMM_PROP]:      [PRACTICE_AREAS.COMM_PROP],
  [ROLES.COCO_LIT]:       [PRACTICE_AREAS.COCO, PRACTICE_AREAS.DISPUTES],
  [ROLES.ADMIN]:          [],
  [ROLES.DIRECTOR]:       [],
  [ROLES.POST_SCANNING]:  [],
  [ROLES.ACCOUNTS]:       [],
};

// Panel IDs
export const PANELS = {
  GOLDEN_RULE:       "golden_rule_pipeline",
  MY_MATTERS:        "my_matters",
  PIPELINE_MINI:     "pipeline_mini",
  DEADLINES_NUDGES:  "deadlines_nudges",
  RESI_CHECKLISTS:   "resi_checklists_completions",
  POST_COMPLETION:   "post_completion_tasks",
  POST_INTAKE_QUEUE: "post_intake_queue",
  FILE_CLOSURE_RISK: "file_closure_risk",
  ACCOUNTS_TODAY:    "accounts_today",
};

// Role → default home page panels
const ROLE_PANELS = {
  [ROLES.ADMIN]:          [PANELS.GOLDEN_RULE, PANELS.FILE_CLOSURE_RISK, PANELS.PIPELINE_MINI],
  [ROLES.DIRECTOR]:       [],
  [ROLES.RESI_FE]:        [PANELS.PIPELINE_MINI, PANELS.RESI_CHECKLISTS],
  [ROLES.RESI_ASSISTANT]: [PANELS.PIPELINE_MINI, PANELS.RESI_CHECKLISTS, PANELS.POST_COMPLETION],
  [ROLES.COMM_PROP]:      [PANELS.PIPELINE_MINI],
  [ROLES.COCO_LIT]:       [PANELS.PIPELINE_MINI],
  [ROLES.POST_SCANNING]:  [PANELS.POST_INTAKE_QUEUE],
  [ROLES.ACCOUNTS]:       [PANELS.ACCOUNTS_TODAY],
};

// Per-email practice area overrides (checked before role-based defaults)
const EMAIL_PRACTICE_AREA_OVERRIDES = {
  "vanessa@bonsai.law": [PRACTICE_AREAS.COCO, PRACTICE_AREAS.DISPUTES],   // "002", "006"
  "rebekah@bonsai.law": [PRACTICE_AREAS.RESI],                            // "001"
  "sharon@bonsai.law":  [PRACTICE_AREAS.IP],                             // "007" — IP fee earner
};

// Per-email panel overrides
// Rebekah = Director + Resi FE: pipeline (resi-filtered) + ResiChecklists + PostCompletion
// Vanessa = Director + CoCo/Disputes FE: pipeline mini (coco/lit filtered)
const EMAIL_PANEL_OVERRIDES = {
  "rebekah@bonsai.law": [PANELS.PIPELINE_MINI, PANELS.RESI_CHECKLISTS, PANELS.POST_COMPLETION],
  "vanessa@bonsai.law": [PANELS.PIPELINE_MINI],
};

// BM-H3660 (5 Oct 2026, Vanessa's decision): directors no longer get the
// firm-wide Accounts Today panel layered onto their dashboard. Only the
// ACCOUNTS role has it (ROLE_PANELS). Finance pages stay in the sidebar.

export function getUserPanels(email) {
  if (!email) return [];
  const normalised = email.toLowerCase().trim();
  let base;
  if (EMAIL_PANEL_OVERRIDES[normalised]) {
    base = EMAIL_PANEL_OVERRIDES[normalised];
  } else {
    const role = getUserRole(email);
    if (!role) return [];
    base = ROLE_PANELS[role] || [];
  }
  return base;
}

export function getUserPracticeAreas(email) {
  if (!email) return [];
  const normalised = email.toLowerCase().trim();
  if (EMAIL_PRACTICE_AREA_OVERRIDES[normalised]) return EMAIL_PRACTICE_AREA_OVERRIDES[normalised];
  const role = getUserRole(email);
  if (!role) return [];
  return ROLE_PRACTICE_AREAS[role] || [];
}

export function isAssistantRole(email) {
  return getUserRole(email) === ROLES.RESI_ASSISTANT;
}

/**
 * PIPELINE_PEERS
 *
 * Controls the pre-filtered pipeline widget on the home screen.
 *
 * email → array of initials to lock the fee earner filter to.
 *   []   = show all matters (unfiltered) — Liezel, clientcare
 *   [..] = show only these fee earners' matters — everyone else
 *   not present in map = no pipeline widget shown — Jonathan, May, directors
 *
 * Directors (Vanessa, Rebekah) intentionally omitted — they use the full
 * /pipeline-tracker page directly; whole-firm view doesn't belong on a homepage.
 */
export const PIPELINE_PEERS = {
  // Residential team
  "rebekah@bonsai.law":    ["RM", "EH", "JD"],
  "emily@bonsai.law":      ["RM", "EH", "JD"],
  "jodie@bonsai.law":      ["RM", "EH", "JD"],
  // Commercial Property
  "sangita@bonsai.law":    ["SG"],
  // CoCo & Litigation
  "vanessa@bonsai.law":    ["VEC", "HD"],
  "himanie@bonsai.law":    ["VEC", "HD"],
  // Admin — unfiltered (empty array = all matters)
  "liezel@bonsai.law":     [],
  "clientcare@bonsai.law": [],
  // Jonathan (post_scanning) and May (accounts) — not in map = no widget
};

/**
 * Returns the locked peer initials for the home pipeline widget.
 *   null  → no pipeline widget for this user
 *   []    → show all matters (admin view)
 *   [...] → pre-filtered to these initials
 */
export function getPipelinePeers(email) {
  if (!email) return null;
  const normalised = email.toLowerCase().trim();
  if (!(normalised in PIPELINE_PEERS)) return null;
  return PIPELINE_PEERS[normalised];
}
